interface FormattingConfig {
    formatValue?: (value: string) => string;
}

const configs: Record<string, FormattingConfig> = {
    'tel': {
        formatValue: (value: string) => {
            if (value.length > 6) {
                return `(${value.substring(0, 3)}) ${value.substring(3, 6)}-${value.substring(6)}`;
            } else if (value.length > 3) {
                return `(${value.substring(0, 3)}) ${value.substring(3)}`;
            } else if (value.length > 0) {
                return `(${value.substring(0, 3)}`;
            }
            return value;
        }
    },
    'zip': {} // No special formatting needed
};

function wireUpFormattedInput(input: HTMLInputElement, type: 'tel' | 'zip') {
    const config = configs[type];
    const originalMaxLength = parseInt(input.getAttribute('maxlength') || '0');
    const validationMessage = input.dataset.validationMessage || `Please enter a valid ${type === 'tel' ? 'phone number' : 'zip code'}`;
    let maxLengthWithFormatting = originalMaxLength;
    
    // For phone numbers, store original maxlength and adjust for formatting
    if (type === 'tel' && originalMaxLength > 0 && !input.hasAttribute('data-maxlength-preformatting')) {
        maxLengthWithFormatting = originalMaxLength + 4;
        input.setAttribute('data-maxlength-preformatting', String(originalMaxLength));
        input.setAttribute('maxlength', String(maxLengthWithFormatting));
    }
    
    input.classList.toggle("formatting-attached", true);

    // Prevent non-numeric input on keydown
    input.addEventListener('keydown', e => {
        if (e.key && !e.ctrlKey && !e.metaKey && e.key.length === 1 && !isFinite(parseInt(e.key))) {
            e.preventDefault();
        }
    });

    // Format the input as it's being typed
    input.addEventListener('input', () => {
        let currentValue = input.value.replace(/\D/g, "");
        
        // Set custom validity if the length isn't correct
        input.setCustomValidity(originalMaxLength && currentValue.length < originalMaxLength ? validationMessage : "");
        
        // Crop the value to the non-formatted maxlength
        if (originalMaxLength) {
            currentValue = currentValue.substring(0, originalMaxLength);
        }

        // Apply custom formatting if specified
        input.value = config.formatValue ? config.formatValue(currentValue) : currentValue;
    });

    // Validate on form submission
    input.form?.addEventListener('submit', e => {
        const unformattedLength = input.value.replace(/\D/g, "").length;
        if (originalMaxLength && unformattedLength !== originalMaxLength) {
            input.setCustomValidity(validationMessage);
            e.preventDefault();
        } else {
            input.setCustomValidity("");
        }
    });
}

function wireUpFormattedInputs(container: ParentNode) {
    // Wire up phone inputs
    for (const input of container.querySelectorAll<HTMLInputElement>("input.formatted-tel[type='tel']:not(.formatting-attached)")) {
        wireUpFormattedInput(input, 'tel');
    }
    
    // Wire up zip inputs
    for (const input of container.querySelectorAll<HTMLInputElement>("input.formatted-zip[type='text']:not(.formatting-attached)")) {
        wireUpFormattedInput(input, 'zip');
    }

	// Wire up email inputs
	const validEmailAddress = /^[\w+\-_][\w+\-_.]*@[\w+\-_][\w+\-_.]*\.[a-z]{2,}$/ui;
	for (const input of container.querySelectorAll<HTMLInputElement>("input[type='email']:not(.formatting-attached)")) {
		input.classList.toggle("formatting-attached", true);

		input.addEventListener('input', () => {
			if (input.validity.customError) {
				input.setCustomValidity(!input.value.match(validEmailAddress) ? "The provided email must be a valid email address" : "");
			}
		});
		input.addEventListener('focusout', () => {
			input.setCustomValidity(!input.value.match(validEmailAddress) ? "The provided email must be a valid email address" : "");
		});

		// Validate on form submission
		input.form?.addEventListener('submit', e => {
			if (!input.value.match(validEmailAddress)) {
				input.setCustomValidity("The provided email must be a valid email address");
				e.preventDefault();
			} else {
				input.setCustomValidity("");
			}
		});
	}
}

wireUpFormattedInputs(document.body);
new MutationObserver(mutations => {
    for (const mutation of mutations) {
        for (const addedNode of mutation.addedNodes) {
            if (addedNode instanceof HTMLInputElement) {
                if (addedNode.classList.contains("formatted-tel") && !addedNode.classList.contains("formatting-attached")) {
                    wireUpFormattedInput(addedNode, 'tel');
                } else if (addedNode.classList.contains("formatted-zip") && !addedNode.classList.contains("formatting-attached")) {
                    wireUpFormattedInput(addedNode, 'zip');
                }
            } else if (addedNode instanceof Element) {
                wireUpFormattedInputs(addedNode);
            }
        }
    }
}).observe(document.body, {childList: true, subtree: true}); 
